/**
 * Attribute Extractor
 *
 * Finds translatable HTML attribute values in the DOM and parses each
 * into an AttributeUnit. Pairs with BlockExtractor (text-content side)
 * to cover everything user-visible:
 *
 *   - <img alt>, <area alt>, <input type="image" alt>
 *   - any element [title]
 *   - <input placeholder>, <textarea placeholder>
 *   - <meta name="description" content="...">
 *   - <input type="submit|button|reset" value>
 *
 * Cross-stack parity: produces the same auto_ key as BlockExtractor for
 * the same trimmed text — md5(text.trim()) prefix — so a button labelled
 * "Submit" and a paragraph "Submit" collide on key (intentional reuse).
 *
 * `<input type="text" value="...">` is deliberately excluded because
 * `value` on a plain text input is user data, not UI copy, and would
 * corrupt form state if treated as translatable.
 */

import type { AttributeKind, AttributeUnit } from '../content/types.js';
import { ExcludeFilter, type ExcludePatterns } from './exclude-filter.js';
import { generateAutoKey } from './md5.js';

interface AttributeTarget {
  /** CSS selector used to find candidate elements */
  selector: string;
  /** The actual HTML attribute name carrying the translatable value */
  attr: string;
  /** Semantic category exposed to the translation pipeline */
  type: AttributeKind;
}

// Each target has a unique `attr`, so a single element matching multiple
// targets (e.g. `<input placeholder title>`) yields one ExtractedAttribute
// per attribute — no cross-target dedup needed. The three `content` targets
// are the one exception, and they are mutually exclusive by construction: a
// <meta> carries one name/property, so it can match at most one of them.
const TARGETS: AttributeTarget[] = [
  { selector: 'img[alt], area[alt], input[type="image"][alt]', attr: 'alt', type: 'alt' },
  { selector: '[title]', attr: 'title', type: 'title' },
  { selector: 'input[placeholder], textarea[placeholder]', attr: 'placeholder', type: 'placeholder' },
  { selector: 'meta[name="description"][content]', attr: 'content', type: 'meta-description' },
  // Social preview text. Without these no auto_ key is ever generated for
  // them, so a fully translated page still shares to Slack or LinkedIn with
  // an English card. Both spellings: OG specifies `property`, Twitter cards
  // conventionally use `name`, and real pages mix the two freely.
  {
    selector:
      'meta[property="og:title"][content], meta[name="og:title"][content], meta[property="twitter:title"][content], meta[name="twitter:title"][content]',
    attr: 'content',
    type: 'title',
  },
  {
    selector:
      'meta[property="og:description"][content], meta[name="og:description"][content], meta[property="twitter:description"][content], meta[name="twitter:description"][content]',
    attr: 'content',
    type: 'meta-description',
  },
  {
    selector:
      'input[type="submit"][value], input[type="button"][value], input[type="reset"][value]',
    attr: 'value',
    type: 'input-value',
  },
];

// Skip the same containers as BlockExtractor — attribute values inside
// <script>, <style>, etc. are never user-visible UI text.
const SKIP_TAGS = new Set([
  'SCRIPT',
  'STYLE',
  'CODE',
  'PRE',
  'NOSCRIPT',
  'SVG',
]);

// Ancestor attributes that opt the element out of translation. Matches
// BlockExtractor's contract so authors get one consistent escape hatch.
const SKIP_ATTRIBUTES = [
  'data-lionrapid-translated',
  'data-lionrapid-client',
  'data-no-translate',
];

/**
 * Server-stamped attribute marker — when the edge or PHP layer has already
 * keyed an attribute, the element carries `data-lr-attr-{name}="auto_xxx"`.
 * Client extraction reads this instead of re-hashing, eliminating the same
 * class of cross-stack parity drift `data-lr-block` solves for body text.
 */
function stampAttrName(attrName: string): string {
  return `data-lr-attr-${attrName.toLowerCase()}`;
}

export interface ExtractedAttribute {
  /** Live element carrying the attribute */
  element: Element;
  /** HTML attribute name (e.g. `alt`, `content`) */
  attrName: string;
  /** Translatable payload */
  unit: AttributeUnit;
  /** auto_ key or server-stamped key */
  key: string;
}

export class AttributeExtractor {
  private excludeFilter: ExcludeFilter;
  private excludeSelectors: string[];

  constructor(
    excludePatterns: Partial<ExcludePatterns> = {},
    excludeSelectors: string[] = [],
    minTextLength = 2
  ) {
    this.excludeFilter = new ExcludeFilter(excludePatterns, minTextLength);
    this.excludeSelectors = excludeSelectors;
  }

  /**
   * Walk the DOM under `root` and return every translatable attribute.
   *
   * @param root           Element or Document to scan
   * @param includeStamped When true, elements already carrying
   *                       `data-lr-attr-{name}` are returned anyway —
   *                       the editor uses this to surface server-handled
   *                       attributes for inspection. The runtime keeps
   *                       the default `false` so it doesn't re-extract
   *                       what the server already keyed.
   */
  extract(root: Element | Document, includeStamped = false): ExtractedAttribute[] {
    const results: ExtractedAttribute[] = [];

    for (const target of TARGETS) {
      const candidates = root.querySelectorAll(target.selector);
      const all: Element[] = [];
      if (root instanceof Element && root.matches(target.selector)) {
        all.push(root);
      }
      for (let i = 0; i < candidates.length; i++) {
        all.push(candidates[i]);
      }

      for (let i = 0; i < all.length; i++) {
        const el = all[i];

        if (this.isInsideSkipTag(el)) continue;
        if (this.hasSkipAncestor(el)) continue;
        if (this.matchesExcludeSelector(el)) continue;
        if (el.getAttribute('translate') === 'no') continue;

        const rawValue = el.getAttribute(target.attr);
        if (rawValue === null) continue;

        const trimmed = rawValue.trim();
        if (this.excludeFilter.shouldExclude(trimmed)) continue;

        const stampName = stampAttrName(target.attr);
        const stamped = el.getAttribute(stampName);
        if (!includeStamped && stamped) {
          continue;
        }

        const unit: AttributeUnit = { text: rawValue, type: target.type };
        const key = stamped ?? generateAutoKey(rawValue);

        results.push({ element: el, attrName: target.attr, unit, key });
      }
    }

    return results;
  }

  private isInsideSkipTag(element: Element): boolean {
    let current: Element | null = element.parentElement;
    while (current) {
      // Case-insensitive: foreign elements (SVG) keep a lowercase tagName.
      if (SKIP_TAGS.has(current.tagName.toUpperCase())) return true;
      current = current.parentElement;
    }
    return false;
  }

  private hasSkipAncestor(element: Element): boolean {
    let current: Element | null = element;
    while (current) {
      for (const attr of SKIP_ATTRIBUTES) {
        if (current.hasAttribute(attr)) return true;
      }
      current = current.parentElement;
    }
    return false;
  }

  private matchesExcludeSelector(element: Element): boolean {
    for (const selector of this.excludeSelectors) {
      try {
        if (element.matches(selector) || element.closest(selector)) {
          return true;
        }
      } catch {
        // Invalid selector — skip silently, same as BlockExtractor.
      }
    }
    return false;
  }
}
